import { observer } from 'mobx-react-lite'
import { type ReactNode } from 'react'
import { ButtonCopy, CopyWrapper, Icon, Typography } from 'shared/ui'
import { DayjsFormats, getAriaLabel } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type AutomationLog } from 'entities/Integrations/model/AutomationLog'
import { AutomationLogResultStatus } from 'entities/Integrations/ui/AutomationLogResultStatus'
import { AutomationLogExecutionStatus } from 'entities/Integrations/ui/AutomationLogExecutionStatus'

import styles from './styles.module.scss'

const Row = ({
  label,
  value,
  copyValue,
}: {
  label: string
  value: string | number | ReactNode
  copyValue?: string
}) => {
  return (
    <div className={styles.field}>
      <div className={styles.label}>
        <Typography
          variant='text-input-field-r'
          ariaLabel={getAriaLabel('sourceLabel')}
          color='var(--content-primary-tertiary)'
        >
          {label}
        </Typography>
      </div>
      <div className={styles.wrapValue}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <CopyWrapper copyValue={value}>
            <Typography
              variant='text-input-field-r'
              ariaLabel={getAriaLabel('sourceValue')}
              color='var(--content-primary-primary)'
              className={styles.value}
              ellipsis
              tooltipProps={{
                label: value,
              }}
              tag='div'
              width={320}
            >
              {value || '-'}
            </Typography>
          </CopyWrapper>
        ) : copyValue ? (
          <CopyWrapper copyValue={copyValue}>
            <div className={styles.value}>{value}</div>
          </CopyWrapper>
        ) : (
          <div className={styles.value}>{value}</div>
        )}
      </div>
    </div>
  )
}

const SourceRow = ({ item }: { item: AutomationLog }) => (
  <div className={styles.sourceRow}>
    {item.sourceIcon && <Icon icon={item.sourceIcon} />}
    <Typography
      variant={'body-md-regular'}
      ellipsis
      tooltipProps={{
        label: item.sourceTitle,
      }}
      tag='div'
      ariaLabel={'name'}
    >
      {item.sourceTitle}
    </Typography>
  </div>
)

export const AutomationLogModalDetailsContent = observer(({ item }: { item: AutomationLog }) => {
  return (
    <div className={styles.wrap}>
      <div className={styles.content}>
        <Row label='Source' value={<SourceRow item={item} />} copyValue={item.sourceTitle || ''} />
        <Row label='ID' value={item.id} />
        <Row label='Entity ID' value={item.entity_id} />
        <Row label='Callback ID' value={item.callback_id} />
        <Row label='Step' value={item.step} />
        <Row label='URL' value={item.url || ''} />
        <Row label='Action' value={item.action} />
        <Row
          label='Execution status'
          value={<AutomationLogExecutionStatus status={item.status} />}
          copyValue={item.status}
        />
        <Row
          label='Result'
          value={
            <AutomationLogResultStatus
              status={item.status}
              failedReason={item.status === 'failed' ? item.failed_reason : null}
              className={styles.resultStatus}
            />
          }
          copyValue={item.failed_reason || item.status}
        />
        <Row
          label='Executed at'
          value={uiStore.dayjs(item.executed_at).format(DayjsFormats.fullWithAtDash2)}
        />
      </div>
      {item.payload && (
        <div className={styles.copyWrap}>
          <div className={styles.label}>
            <Typography
              variant='text-input-field-r'
              ariaLabel={getAriaLabel('sourceLabel')}
              color='var(--content-primary-tertiary)'
            >
              Request data
            </Typography>
            <ButtonCopy value={item.payload || ''} />
          </div>
          <div className={styles.copyContent}>
            <Typography
              variant='body-rg-regular'
              ariaLabel={getAriaLabel('sourceLabel')}
              color='var(--content-primary-primary)'
            >
              {item.payload}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
})
